<div class="flex flex-col gap-5 flex-1 min-h-0 relative">

    <!-- Loading Overlay -->
    <div *ngIf="isLoading"
        class="absolute inset-0 z-[100] flex items-center justify-center bg-white/40 dark:bg-slate-900/40 backdrop-blur-[2px]">
        <div class="flex flex-col items-center gap-4">
            <div class="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span
                class="text-xs font-black text-blue-600 dark:text-cyan-400 uppercase tracking-widest animate-pulse">Filtering
                Data...</span>
        </div>
    </div>

    <!-- Advanced Filters & Visibility -->
    <div
        class="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 shadow-sm flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex flex-wrap items-center gap-6">
                <!-- Time Scale Selector -->
                <div class="flex flex-col gap-1">
                    <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Time Scale</label>
                    <p-selectbutton [options]="timeScales" [(ngModel)]="selectedTimeScale" (onChange)="onTimeScaleChange()"
                        [styleClass]="'premium-select-button'"></p-selectbutton>
                    </div>
                <!-- Date Picker -->
                <div class="flex flex-col gap-1">
                    <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        {{ selectedTimeScale === 'Day' ? 'Select Date' : selectedTimeScale === 'Week' ? 'End Date' : selectedTimeScale ===
                        'Month' ? 'Select Month' : 'Select Year' }}
                    </label>
                    <p-datepicker [(ngModel)]="selectedDate" (onSelect)="onDateChange()" [showIcon]="true" [iconDisplay]="'input'"
                        [view]="selectedTimeScale === 'Year' ? 'year' : (selectedTimeScale === 'Month' ? 'month' : 'date')"
                        [dateFormat]="selectedTimeScale === 'Year' ? 'yy' : (selectedTimeScale === 'Month' ? 'mm/yy' : 'dd/mm/yy')"
                        styleClass="w-48" appendTo="body"></p-datepicker>
                </div>
            </div>
            
            <!-- Search -->
            <div class="flex items-center">
                <div class="relative">
                    <input type="text" [(ngModel)]="searchTerm" (input)="onSearchChange()" placeholder="Search records..."
                        class="pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-bold text-slate-800 dark:text-white w-64 focus:ring-2 focus:ring-blue-500 transition-all outline-none placeholder:text-slate-400">
                    <i class="fa-solid fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                </div>
            </div>

            <!-- Export Actions -->
            <div class="flex items-center gap-3">
                <button (click)="exportExcel()"
                    class="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-500/20 active:scale-95">
                    <i class="fa-solid fa-file-excel"></i>
                    <span>Excel</span>
                </button>
                <button (click)="exportPDF()"
                    class="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-rose-700 transition-all shadow-lg shadow-rose-500/20 active:scale-95">
                    <i class="fa-solid fa-file-pdf"></i>
                    <span>PDF</span>
                </button>
                <button (click)="exportCSV()"
                    class="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 active:scale-95">
                    <i class="fa-solid fa-download"></i>
                    <span>CSV</span>
                </button>
            </div>
        </div>

        <!-- Parameter Toggles -->
        <div class="pt-4 border-t border-slate-100 dark:border-slate-800">
            <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">Visible Parameters</span>
            <div class="flex flex-wrap gap-2">
                <button *ngFor="let col of reportColumns" (click)="toggleColumn(col.key)"
                    class="px-4 py-2 rounded-lg text-[11px] font-black uppercase tracking-wider border transition-all flex items-center gap-2"
                    [ngClass]="col.visible
                    ? 'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-400 shadow-sm'
                    : 'bg-white border-slate-200 text-slate-400 dark:bg-slate-900 dark:border-slate-800 opacity-60 hover:opacity-100'">
                    <i class="fa-solid" [ngClass]="col.visible ? 'fa-check-circle' : 'fa-circle-dot'"></i>
                    {{ getColumnLabel(col) }}
                </button>
            </div>
        </div>
    </div>

    <!-- Data Table -->
    <div
        class="flex-1 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col min-h-0">
        <div class="flex-1 overflow-auto custom-scrollbar">
            <table class="w-full text-left border-collapse">
                <thead
                    class="bg-slate-50/80 dark:bg-slate-950/80 backdrop-blur-md sticky top-0 z-20 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                        <th class="p-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Timestamp</th>
                        <ng-container *ngFor="let col of reportColumns">
                            <th *ngIf="col.visible" class="p-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                                {{ getColumnLabel(col) }}
                            </th>
                        </ng-container>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                    <tr *ngFor="let row of paginatedData" class="group hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-colors">
                        <td class="p-4">
                            <div class="flex flex-col gap-0.5 whitespace-nowrap">
                                <span class="text-xs font-black text-slate-800 dark:text-white" [innerHTML]="row.date | highlight:searchTerm"></span>
                                <span class="text-[10px] font-bold text-slate-400 font-mono" [innerHTML]="row.time | highlight:searchTerm"></span>
                            </div>
                        </td>
                        <ng-container *ngFor="let col of reportColumns">
                            <td *ngIf="col.visible" class="p-4">
                                <span class="text-sm font-bold text-slate-700 dark:text-slate-200"
                                    [innerHTML]="row[col.key] | highlight:searchTerm"></span>
                            </td>
                        </ng-container>
                    </tr>
                    <tr *ngIf="reportData.length === 0 && !isLoading">
                        <td [attr.colspan]="reportColumns.length + 1" class="p-20 text-center">
                            <div class="flex flex-col items-center gap-3 opacity-20 dark:invert">
                                <i class="fa-solid fa-folder-open text-5xl"></i>
                                <span class="text-xs font-black uppercase tracking-widest">No Environmental Records for this Period</span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination Controls -->
        <div
            class="border-t border-slate-200 dark:border-slate-800 p-4 flex flex-wrap items-center justify-between flex-shrink-0 bg-white dark:bg-slate-900 gap-4">
            <div class="flex items-center gap-3">
                <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Show Records:</span>
                <div class="flex gap-1.5">
                    <button *ngFor="let option of itemsPerPageOptions" (click)="setItemsPerPage(option)"
                        class="px-3 py-1.5 rounded-lg text-xs font-bold transition-all border"
                        [ngClass]="itemsPerPage === option
                        ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-blue-400'">
                        {{ option }}
                    </button>
                </div>
            </div>

            <div class="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                Page <span class="text-slate-800 dark:text-white">{{ currentPage }}</span> of {{ totalPages }} | <span
                    class="text-blue-600">{{ filteredData.length }}</span> Total Logs
            </div>

            <div class="flex items-center gap-2">
                <button (click)="firstPage()" [disabled]="currentPage === 1"
                    class="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    title="First Page">
                    <i class="fa-solid fa-angles-left text-xs"></i>
                </button>

                <button (click)="prevPage()" [disabled]="currentPage === 1"
                    class="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    title="Previous Page">
                    <i class="fa-solid fa-chevron-left text-xs"></i>
                </button>

                <div class="flex gap-1">
                    <button *ngFor="let page of pageNumbers" (click)="goToPage(page)"
                        class="w-9 h-9 flex items-center justify-center rounded-xl text-xs font-black transition-all border"
                        [ngClass]="currentPage === page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-blue-400'">
                        {{ page }}
                    </button>
                </div>

                <button (click)="nextPage()" [disabled]="currentPage === totalPages"
                    class="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    title="Next Page">
                    <i class="fa-solid fa-chevron-right text-xs"></i>
                </button>
<button (click)="lastPage()" [disabled]="currentPage === totalPages"
    class="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
    title="Last Page">
    <i class="fa-solid fa-angles-right text-xs"></i>
</button>
            </div>
        </div>
    </div>
    </div>